import {Add} from '@mui/icons-material';
import {Box, Button, Grid, Typography, useTheme} from '@mui/material';
import {useCanAll, useCan, usePersona} from 'src/permissions/hooks';
import {FormattedMessage, useIntl} from 'react-intl';
import {usersApi} from 'src/api';
import AMPModal from 'src/components/common/AMPModal';
import {actionsWrapperSx, addReManagerButtonSx, formSx} from './AddReManager.sx';
import {useState} from 'react';
import {Form, Formik, FormikProps} from 'formik';
import {useBusinessId, useAppSelector} from 'src/app/hooks';
import {AMPFormSelect} from 'src/components/common/AMPFormSelect';
import {getHelperText} from 'src/utils/forms';
import {AMPInput} from 'src/components/common/AMPInput';
import {CancelButton} from 'src/components/common/CancelButton';
import {SubmitButton} from 'src/components/common/SubmitButton';
import {isErrorObject, processedLanguages} from 'src/utils/helper';
import {AddReManagerInitialValue, reManagerFormSchema, ReManagerFormType,} from '../formData/reOwners';
import {ReOwnersDropdown} from '../ReOwnersForm/ReOwnersDropdown';
import {NotifyError} from 'src/utils/notification';
import {useParams} from 'react-router-dom';
import {UserTypeEnum} from 'src/enums/common';

export const AddReManager = () => {
    // Both, because the endpoint requires both: creating the account and giving it a role are one
    // action here, and holding half of it achieves nothing.
    const canAddManager = useCanAll('users.create', 'accessControl.manageBindings');
    const intl = useIntl();

    const theme = useTheme();

    const {id: reOwnerId} = useParams();

    const {authInfo} = useAppSelector((state) => state.common);
    const defaultBusinessId = useBusinessId();
    const dialogTitle = intl.formatMessage({id: 'addReManager'});

    const [open, setOpen] = useState<boolean>(false);

    const [createManagerTrigger] = usersApi.useCreateManagerMutation();

    const handleSubmit = async (values: ReManagerFormType) => {
        const {emailAddress, firstName, lastName, realEstateId, businessId, language} = values;

        try {
            await createManagerTrigger({
                data: {
                    email: emailAddress,
                    firstName,
                    lastName,
                    realEstateId,
                    userType: UserTypeEnum.REAL_ESTATE,
                    language,
                },
                businessId,
            }).unwrap();
        } catch (error) {
            if (isErrorObject(error)) {
                NotifyError(error.data.message);
            }
        } finally {
            handleCloseModal();
        }
    };

    const handleOpenModal = () => {
        setOpen(true);
    };

    const handleCloseModal = () => {
        setOpen(false);
    };

    if (!canAddManager) {
        return null;
    }

    return (
        <>
            <Button
                sx={addReManagerButtonSx(theme)}
                variant="contained"
                startIcon={<Add/>}
                onClick={handleOpenModal}
            >
                <FormattedMessage id={'addReManager'}/>
            </Button>

            <AMPModal
                handleClose={handleCloseModal}
                open={open}
                title={dialogTitle}
                type="md"
                fullWidth
                withoutPaddings
            >
                <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                    <Formik
                        // The business being worked in, for everyone. The administrator used to
                        // start from nothing and pick one in a field that no longer exists.
                        initialValues={{
                            ...AddReManagerInitialValue,
                            businessId: defaultBusinessId,
                            realEstateId: reOwnerId ? parseInt(reOwnerId) : 0,
                        }}
                        validationSchema={reManagerFormSchema}
                        onSubmit={handleSubmit}
                    >
                        {({
                              errors,
                              handleBlur,
                              handleChange,
                              handleSubmit,
                              isSubmitting,
                              touched,
                              values,
                          }: FormikProps<ReManagerFormType>) => (
                            <Form
                                onSubmit={handleSubmit}
                                noValidate
                            >
                                <Box sx={formSx(theme)}>
                                    {/* No business field: the manager is created in the
                                        business being worked in. */}

                                    {/* RE Owner */}
                                    <Grid
                                        lg={12}
                                        item
                                    >
                                        <ReOwnersDropdown business={values.businessId}/>
                                    </Grid>

                                    {/* First Name */}
                                    <Grid
                                        lg={12}
                                        item
                                    >
                                        <AMPInput
                                            value={values['firstName']}
                                            type={'firstName'}
                                            label={'firstName'}
                                            input={'firstName'}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            hasError={touched['firstName'] && Boolean(errors['firstName'])}
                                            helperText={getHelperText('firstName', touched, errors, intl)}
                                        />
                                    </Grid>

                                    {/* Last Name */}
                                    <Grid
                                        lg={12}
                                        item
                                    >
                                        <AMPInput
                                            value={values['lastName']}
                                            type={'lastName'}
                                            label={'lastName'}
                                            input={'lastName'}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            hasError={touched['lastName'] && Boolean(errors['lastName'])}
                                            helperText={getHelperText('lastName', touched, errors, intl)}
                                        />
                                    </Grid>

                                    {/* Email address */}
                                    <Grid
                                        lg={12}
                                        item
                                    >
                                        <AMPInput
                                            value={values['emailAddress']}
                                            type={'emailAddress'}
                                            label={'emailAddress'}
                                            input={'emailAddress'}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            hasError={touched['emailAddress'] && Boolean(errors['emailAddress'])}
                                            helperText={getHelperText('emailAddress', touched, errors, intl)}
                                        />
                                    </Grid>

                                    {/* Language */}
                                    <AMPFormSelect
                                        data={processedLanguages(intl)}
                                        value={values['language'] as string}
                                        label={'language'}
                                        input={'language'}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        hasError={touched['language'] && Boolean(errors['language'])}
                                        helperText={getHelperText('language', touched, errors, intl)}
                                    />
                                </Box>

                                <Box sx={actionsWrapperSx(theme)}>
                                    <CancelButton
                                        text="cancel"
                                        handler={handleCloseModal}
                                    />
                                    <SubmitButton
                                        text={'createUser'}
                                        color="primary"
                                        isSubmitting={isSubmitting}
                                    />
                                </Box>
                            </Form>
                        )}
                    </Formik>
                </Box>
            </AMPModal>
        </>
    );
};
